import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './components/ui/select';
import { Plus, FileText, TrendingUp, Eye, Edit, Trash2, Archive, Calendar } from 'lucide-react';
import { MultiStepWalkthroughForm } from './components/MultiStepWalkthroughForm';
import { ZoneTaskMatrixEditor } from './components/ZoneTaskMatrixEditor';
import { BidBreakdown } from './components/BidBreakdown';
import { WalkthroughAttachments } from './components/WalkthroughAttachments';
import { toast } from 'sonner';
import { RecordContextHeader } from './components/RecordContextHeader';
import { EmptyState } from './components/EmptyState';
import { NextStepPrompt } from './components/NextStepPrompt';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const getToken = () => localStorage.getItem('token');

const api = async (endpoint, options = {}) => {
  const token = getToken();
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...options.headers
  };
  const response = await fetch(`${API_URL}${endpoint}`, { ...options, headers });
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

export function EnhancedBiddingPage() {
  const [leads, setLeads] = useState([]);
  const [walkthroughs, setWalkthroughs] = useState([]);
  const [bids, setBids] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [showWalkthroughForm, setShowWalkthroughForm] = useState(false);
  const [selectedBid, setSelectedBid] = useState(null);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [showProposalCreatedPrompt, setShowProposalCreatedPrompt] = useState(false);
  const [createdProposalId, setCreatedProposalId] = useState(null);
  const [proposalFormData, setProposalFormData] = useState({
    services: 'Standard janitorial cleaning',
    notes: '',
    exclusions: ''
  });
  const [bidSortBy, setBidSortBy] = useState('newest');
  const [selectedWalkthrough, setSelectedWalkthrough] = useState(null);
  const [editingWalkthrough, setEditingWalkthrough] = useState(null);
  const [showArchivedBids, setShowArchivedBids] = useState(false);
  const [archivedBids, setArchivedBids] = useState([]);
  const [showZoneEditor, setShowZoneEditor] = useState(false);
  const [zoneEditorWalkthroughId, setZoneEditorWalkthroughId] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [leadsData, walkthroughsData, bidsData, proposalsData, archivedBidsData] = await Promise.all([
        api('/api/leads'),
        api('/api/walkthroughs'),
        api('/api/bids'),
        api('/api/proposals'),
        api('/api/bids?include_archived=true')
      ]);
      setLeads(leadsData);
      setWalkthroughs(walkthroughsData);
      setBids(bidsData);
      setProposals(proposalsData);
      setArchivedBids(archivedBidsData.filter(b => b.archived === true));
    } catch (error) {
      toast.error('Failed to load data');
    }
  };

  const handleWalkthroughSave = () => {
    setShowWalkthroughForm(false);
    setEditingWalkthrough(null);
    loadData();
  };

  const handleViewWalkthrough = (wt) => {
    setSelectedWalkthrough(wt);
    setEditingWalkthrough(null);
  };

  const handleEditWalkthrough = (wt) => {
    setEditingWalkthrough(wt);
    setSelectedWalkthrough(null);
    setShowWalkthroughForm(true);
    setShowZoneEditor(false);
  };

  const handleManageZones = (wtId) => {
    setZoneEditorWalkthroughId(wtId);
    setShowZoneEditor(true);
    setShowWalkthroughForm(false);
    setSelectedWalkthrough(null);
  };

  const handleDeleteWalkthrough = async (wtId) => {
    if (!window.confirm('Delete this walkthrough? This cannot be undone.')) return;
    try {
      await api(`/api/walkthroughs/${wtId}`, { method: 'DELETE' });
      toast.success('Walkthrough deleted');
      if (selectedWalkthrough?.id === wtId) setSelectedWalkthrough(null);
      if (editingWalkthrough?.id === wtId) setEditingWalkthrough(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete walkthrough');
    }
  };

  const handleCalculateBid = async (walkthroughId) => {
    try {
      const bidData = await api('/api/bids/calculate', {
        method: 'POST',
        body: JSON.stringify({ walkthrough_id: walkthroughId })
      });
      toast.success('Bid calculated successfully');
      setSelectedBid(bidData);
      loadData();
    } catch (error) {
      toast.error('Failed to calculate bid');
    }
  };

  const handleDeleteBid = async (bidId) => {
    if (!window.confirm('Delete this bid? This cannot be undone.')) return;
    try {
      await api(`/api/bids/${bidId}`, { method: 'DELETE' });
      toast.success('Bid deleted');
      if (selectedBid?.id === bidId) setSelectedBid(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete bid');
    }
  };

  const handleArchiveBid = async (bidId) => {
    if (!window.confirm('Archive this bid? You can view archived bids later.')) return;
    try {
      await api(`/api/bids/${bidId}/archive`, { method: 'PUT' });
      toast.success('Bid archived');
      if (selectedBid?.id === bidId) setSelectedBid(null);
      loadData();
    } catch (error) {
      toast.error('Failed to archive bid');
    }
  };

  const handleUnarchiveBid = async (bidId) => {
    try {
      await api(`/api/bids/${bidId}/unarchive`, { method: 'PUT' });
      toast.success('Bid restored');
      loadData();
    } catch (error) {
      toast.error('Failed to restore bid');
    }
  };

  const handleCreateProposal = async (bidId) => {
    setSelectedBid(bids.find(b => b.id === bidId));
    setShowProposalForm(true);
  };

  const handleProposalSubmit = async () => {
    if (!selectedBid) return;
    try {
      const response = await api(`/api/bids/${selectedBid.id}/create-proposal`, {
        method: 'POST',
        body: JSON.stringify(proposalFormData)
      });
      toast.success('Proposal created');
      setShowProposalForm(false);
      setProposalFormData({ services: 'Standard janitorial cleaning', notes: '', exclusions: '' });
      setSelectedBid(null);
      setCreatedProposalId(response.id);
      setShowProposalCreatedPrompt(true);
      loadData();
    } catch (error) {
      toast.error('Failed to create proposal');
    }
  };

  const getLeadForWalkthrough = (leadId) => leads?.find(l => l.id === leadId) || null;
  const getBidForWalkthrough = (walkthroughId) => bids?.find(b => b.walkthrough_id === walkthroughId) || null;
  const getProposalForBid = (bidId) => proposals?.find(p => p.bid_id === bidId) || null;

  const getSortedBids = () => {
    const sorted = [...(bids || [])];
    switch (bidSortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
      case 'highest':
        return sorted.sort((a, b) => (b.summary?.recommended_monthly_price || 0) - (a.summary?.recommended_monthly_price || 0));
      case 'lowest':
        return sorted.sort((a, b) => (a.summary?.recommended_monthly_price || 0) - (b.summary?.recommended_monthly_price || 0));
      default:
        return sorted;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  return (
    <div className="space-y-6">
      {/* Proposal Created Prompt */}
      {showProposalCreatedPrompt && (
        <NextStepPrompt
          title="Proposal Created!"
          message="Your proposal is ready. What's next?"
          primaryAction="View Proposal"
          secondaryAction="Continue"
          onPrimaryClick={() => {
            setShowProposalCreatedPrompt(false);
            window.location.href = '#proposals';
          }}
          onSecondaryClick={() => setShowProposalCreatedPrompt(false)}
        />
      )}

      <div>
        <h1 className="text-3xl font-[Space_Grotesk] font-semibold tracking-tight">Bidding</h1>
        <p className="text-muted-foreground">Manage walkthroughs, bids, and proposals</p>
      </div>

      {showProposalForm && selectedBid && (
        <Card>
          <CardHeader>
            <CardTitle>Create Proposal</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Services</label>
              <textarea
                className="w-full border rounded p-2 text-sm"
                rows={3}
                value={proposalFormData.services}
                onChange={(e) => setProposalFormData({...proposalFormData, services: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Notes</label>
              <textarea
                className="w-full border rounded p-2 text-sm"
                rows={2}
                value={proposalFormData.notes}
                onChange={(e) => setProposalFormData({...proposalFormData, notes: e.target.value})}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Exclusions</label>
              <textarea
                className="w-full border rounded p-2 text-sm"
                rows={2}
                value={proposalFormData.exclusions}
                onChange={(e) => setProposalFormData({...proposalFormData, exclusions: e.target.value})}
              />
            </div>
            <div className="flex gap-2">
              <Button onClick={handleProposalSubmit}>Create Proposal</Button>
              <Button variant="outline" onClick={() => { setShowProposalForm(false); setSelectedBid(null); }}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedBid && !showProposalForm && (
        <div>
          {/* Context Header */}
          {(() => {
            const walkthrough = walkthroughs?.find(w => w.id === selectedBid.walkthrough_id);
            const lead = walkthrough ? leads?.find(l => l.id === walkthrough.lead_id) : null;
            return lead && (
              <RecordContextHeader
                label="Bid Context"
                company={lead.company_name}
                facility={walkthrough.facility_name || `${walkthrough.square_footage} sq ft facility`}
                status="bid_calculated"
                additionalInfo={`$${selectedBid.summary?.recommended_monthly_price || 0}/mo`}
              />
            );
          })()}
          <Button variant="outline" size="sm" onClick={() => setSelectedBid(null)} className="mb-4">
            ← Back to List
          </Button>
          <BidBreakdown bid={selectedBid} />
        </div>
      )}

      {!showProposalForm && !selectedBid && (
        <Tabs defaultValue="walkthroughs" className="w-full">
          <TabsList>
            <TabsTrigger value="walkthroughs">Walkthroughs ({walkthroughs?.length || 0})</TabsTrigger>
            <TabsTrigger value="bids">Bids ({bids?.length || 0})</TabsTrigger>
            <TabsTrigger value="proposals">Proposals ({proposals?.length || 0})</TabsTrigger>
          </TabsList>

          {/* WALKTHROUGHS TAB */}
          <TabsContent value="walkthroughs" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Walkthroughs</h2>
              <Button onClick={() => setShowWalkthroughForm(!showWalkthroughForm)} data-testid="new-walkthrough-button">
                <Plus className="h-4 w-4 mr-2" />
                New Walkthrough
              </Button>
            </div>

            {showWalkthroughForm && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <MultiStepWalkthroughForm
                    walkthroughId={editingWalkthrough?.id}
                    leadId={editingWalkthrough?.lead_id || (leads && leads.length > 0 ? leads[0]?.id : '')}
                    onSave={handleWalkthroughSave}
                    onCancel={() => {
                      setShowWalkthroughForm(false);
                      setEditingWalkthrough(null);
                    }}
                  />
                </CardContent>
              </Card>
            )}

            {showZoneEditor && zoneEditorWalkthroughId && (
              <Card className="mb-6">
                <CardContent className="pt-6">
                  <div className="space-y-4">
                    <div className="flex justify-between items-center">
                      <h3 className="text-xl font-semibold">Manage Zones & Tasks</h3>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowZoneEditor(false);
                          setZoneEditorWalkthroughId(null);
                        }}
                      >
                        Close Editor
                      </Button>
                    </div>
                    <ZoneTaskMatrixEditor
                      walkthroughId={zoneEditorWalkthroughId}
                      buildingType={(walkthroughs && walkthroughs.find(w => w.id === zoneEditorWalkthroughId)?.building_type) || 'office'}
                      onUpdate={loadData}
                    />
                  </div>
                </CardContent>
              </Card>
            )}

            {selectedWalkthrough && !showWalkthroughForm && (
              <Card className="mb-6">
                <CardHeader>
                  <div className="flex justify-between items-center">
                    <CardTitle>Walkthrough Details</CardTitle>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => handleEditWalkthrough(selectedWalkthrough)} data-testid="edit-selected-walkthrough">
                        <Edit className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => setSelectedWalkthrough(null)}>
                        Close
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Company</p>
                        <p className="text-base">{getLeadForWalkthrough(selectedWalkthrough.lead_id)?.company_name || 'Unknown'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Building Type</p>
                        <p className="text-base capitalize">{selectedWalkthrough.building_type?.toString().replace('_', ' ')}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Square Footage</p>
                        <p className="text-base">{selectedWalkthrough.square_footage} sq ft</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Floors</p>
                        <p className="text-base">{selectedWalkthrough.floors}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Floor Types</p>
                        <p className="text-base capitalize">{Array.isArray(selectedWalkthrough.floor_types) ? selectedWalkthrough.floor_types.join(', ').replace(/_/g, ' ') : (selectedWalkthrough.floor_types?.toString().replace(/_/g, ' ') || 'N/A')}</p>
                      </div>
                    </div>
                    
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Frequency</p>
                        <p className="text-base capitalize">{selectedWalkthrough.frequency?.toString().replace('_', ' ') || 'N/A'}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Condition</p>
                        <p className="text-base capitalize">{selectedWalkthrough.condition}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Restrooms</p>
                        <p className="text-base">{selectedWalkthrough.num_restrooms}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Breakrooms</p>
                        <p className="text-base">{selectedWalkthrough.num_breakrooms || 0}</p>
                      </div>
                      <div>
                        <p className="text-sm font-semibold text-muted-foreground">Private Offices</p>
                        <p className="text-base">{selectedWalkthrough.num_offices || 0}</p>
                      </div>
                    </div>

                    {selectedWalkthrough.notes && (
                      <div className="md:col-span-2">
                        <p className="text-sm font-semibold text-muted-foreground">Notes</p>
                        <p className="text-base">{selectedWalkthrough.notes}</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attachments Section */}
            {selectedWalkthrough && !showWalkthroughForm && (
              <div className="mb-6">
                <WalkthroughAttachments walkthroughId={selectedWalkthrough.id} />
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(walkthroughs || []).map((wt) => {
                const lead = getLeadForWalkthrough(wt.lead_id);
                const bid = getBidForWalkthrough(wt.id);
                return (
                  <Card key={wt.id} className={bid ? 'border-teal-200 bg-teal-50/30' : ''} data-testid="walkthrough-card">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <p className="font-medium text-sm">{lead?.company_name || 'Unknown Lead'}</p>
                          {bid && <Badge variant="outline" className="bg-teal-100 text-teal-800 border-teal-300 text-xs">✓ Bid</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {wt.square_footage} sq ft · {wt.building_type?.toString().replace('_', ' ')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {wt.num_restrooms} restrooms · {wt.frequency} · {wt.condition}
                        </p>
                        <p className="text-xs text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(wt.created_at)}
                        </p>
                        <div className="flex flex-wrap gap-1 pt-2 border-t">
                          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handleViewWalkthrough(wt)} data-testid="view-walkthrough-button">
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handleEditWalkthrough(wt)}>
                            <Edit className="h-3 w-3 mr-1" />
                            Edit
                          </Button>
                          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => handleManageZones(wt.id)} data-testid="manage-zones-button">
                            <FileText className="h-3 w-3 mr-1" />
                            Zones
                          </Button>
                        </div>
                        <div className="flex gap-1">
                          {!bid && (
                            <div className="flex-1">
                              <Button 
                                size="sm" 
                                className="w-full text-xs" 
                                onClick={() => handleCalculateBid(wt.id)} 
                                data-testid="calculate-bid-button"
                                disabled={!wt.square_footage || !wt.building_type}
                              >
                                Calculate Bid
                              </Button>
                              {(!wt.square_footage || !wt.building_type) && (
                                <p className="text-xs text-muted-foreground mt-1">
                                  Complete walkthrough data required
                                </p>
                              )}
                            </div>
                          )}
                          <Button size="sm" variant="ghost" className="flex-1 text-xs text-destructive" onClick={() => handleDeleteWalkthrough(wt.id)} data-testid="delete-walkthrough-button">
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {(walkthroughs?.length === 0 || !walkthroughs) && (
                <div className="col-span-full">
                  <EmptyState
                    icon={Calendar}
                    title="No Walkthroughs Yet"
                    message="Create your first walkthrough to start the bidding process. Capture facility details and room counts to generate accurate bids."
                    actionLabel="New Walkthrough"
                    onActionClick={() => setShowWalkthroughForm(true)}
                  />
                </div>
              )}
            </div>
          </TabsContent>

          {/* BIDS TAB */}
          <TabsContent value="bids" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Bids</h2>
              <div className="flex items-center gap-2">
                <label className="text-sm text-muted-foreground">Sort:</label>
                <Select value={bidSortBy} onValueChange={setBidSortBy}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="highest">Highest Price</SelectItem>
                    <SelectItem value="lowest">Lowest Price</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {getSortedBids().map((bid) => {
                const proposal = getProposalForBid(bid.id);
                const walkthrough = walkthroughs?.find(w => w.id === bid.walkthrough_id);
                const lead = walkthrough ? getLeadForWalkthrough(walkthrough.lead_id) : null;
                return (
                  <Card key={bid.id} className={proposal ? 'border-emerald-200 bg-emerald-50/30' : 'bg-gradient-to-br from-teal-50 to-emerald-50'} data-testid="bid-card">
                    <CardContent className="p-4">
                      <div className="space-y-2">
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <p className="font-semibold text-lg tabular-nums">
                              ${bid.summary?.recommended_monthly_price}/mo
                            </p>
                            <p className="text-sm font-medium text-foreground">{lead?.company_name || 'Unknown Company'}</p>
                            <p className="text-xs text-muted-foreground">Contact: {lead?.contact_name || 'N/A'}</p>
                          </div>
                          <Badge variant="outline" className="bg-white text-xs">
                            {bid.summary?.profit_margin_pct}%
                          </Badge>
                        </div>
                        
                        <div className="space-y-1 pt-2 border-t">
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium">Walkthrough:</span> {walkthrough ? formatDate(walkthrough.created_at) : 'N/A'}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium">Created:</span> {formatDate(bid.created_at)}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium">Range:</span> {bid.summary?.price_range}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            <span className="font-medium">Hours/mo:</span> {bid.summary?.estimated_monthly_hours}
                          </p>
                        </div>
                        
                        {proposal && (
                          <Badge variant="outline" className="bg-emerald-100 text-emerald-800 border-emerald-300 text-xs">
                            ✓ Proposal Created
                          </Badge>
                        )}
                        <div className="flex gap-1 mt-3 pt-3 border-t">
                          <Button size="sm" variant="outline" className="flex-1 text-xs" onClick={() => setSelectedBid(bid)} data-testid="view-bid-button">
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                          {!proposal && (
                            <Button size="sm" className="flex-1 text-xs" onClick={() => handleCreateProposal(bid.id)} data-testid="create-proposal-button">
                              <Plus className="h-3 w-3 mr-1" />
                              Proposal
                            </Button>
                          )}
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" className="flex-1 text-xs" onClick={() => handleDeleteBid(bid.id)} data-testid="delete-bid-button">
                            <Trash2 className="h-3 w-3 mr-1" />
                            Delete
                          </Button>
                          <Button size="sm" variant="ghost" className="flex-1 text-xs text-muted-foreground" onClick={() => handleArchiveBid(bid.id)} data-testid="archive-bid-button">
                            <Archive className="h-3 w-3 mr-1" />
                            Archive
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
              {(bids?.length === 0 || !bids) && (
                <div className="col-span-full">
                  <EmptyState
                    icon={TrendingUp}
                    title="No Bids Yet"
                    message="Calculate bids from your walkthroughs to generate pricing proposals. Make sure walkthrough data is complete before calculating."
                    actionLabel="View Walkthroughs"
                    onActionClick={() => document.querySelector('[value="walkthroughs"]')?.click()}
                  />
                </div>
              )}
            </div>
          </TabsContent>

          {/* PROPOSALS TAB */}
          <TabsContent value="proposals" className="mt-6">
            <div className="mb-4">
              <h2 className="text-xl font-semibold">Proposals</h2>
              <p className="text-sm text-muted-foreground">View all proposals or go to Proposals page for full management</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {(proposals || []).slice(0, 6).map((proposal) => (
                <Card key={proposal.id} className={`border-2 ${proposal.status === 'won' ? 'border-success bg-emerald-50' : 'border-amber-200 bg-amber-50/30'}`}>
                  <CardContent className="p-4">
                    <div className="space-y-2">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-sm">{proposal.company_name}</p>
                          <p className="text-lg font-semibold tabular-nums text-brand">
                            ${proposal.monthly_price}/mo
                          </p>
                        </div>
                        <Badge className={`status-${proposal.status} text-xs`}>
                          {proposal.status}
                        </Badge>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {proposal.frequency?.toString().replace('_', ' ')}
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {formatDate(proposal.created_at)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
              {(proposals?.length === 0 || !proposals) && (
                <div className="col-span-full">
                  <EmptyState
                    icon={FileText}
                    title="No Proposals Yet"
                    message="Create professional proposals from your calculated bids. Proposals can be sent to clients and tracked through the sales process."
                    actionLabel="View Bids"
                    onActionClick={() => document.querySelector('[value="bids"]')?.click()}
                  />
                </div>
              )}
              {proposals && proposals.length > 6 && (
                <Card className="col-span-full bg-slate-50">
                  <CardContent className="p-6 text-center">
                    <p className="text-sm text-muted-foreground">
                      Showing {Math.min(6, proposals?.length || 0)} of {proposals?.length || 0} proposals. 
                      <a href="#" className="text-brand ml-1">Go to Proposals page →</a>
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
